<template>
  <div class="bsp-container">
    <BSPHeader />

    <template v-if="!callActive">
      <BSPSetup
        @start-call="handleStartCall"
        @call-started="handleCallStarted"
      />
    </template>

    <template v-else>
      <BSPCall
        :is-listening="isListening"
        :transcript="transcript"
        @end-call="handleEndCall"
      />
    </template>
  </div>
</template>

<script>
import bspApi from "@/services/api/bspApi"
import BSPHeader from "./components/BSPHeader.vue"
import BSPSetup from "./components/BSPSetup.vue"
import BSPCall from "./components/BSPCall.vue"

export default {
  name: "BSPView",
  components: {
    BSPHeader,
    BSPSetup,
    BSPCall
  },
  data () {
    return {
      callActive: false,
      isListening: false,
      transcript: [],
      peerConnection: null,
      dataChannel: null,
      audioElement: null,
      mediaStream: null,
      agreeability: null,
      productContext: null,
      sessionReady: false,
      sessionSetupPromise: null,
      sessionSetupResolver: null
    }
  },
  created () {
    this.sessionSetupPromise = new Promise((resolve) => {
      this.sessionSetupResolver = resolve
    })
  },
  methods: {
    handleCallStarted () {
      // This method will be called when the call is successfully started
      this.$emit("call-started")
    },
    async handleStartCall ({ agreeability, productContext }) {
      try {
        // Store agreeability and product context for later use
        this.agreeability = agreeability
        this.productContext = productContext

        // Initialize BSP session and get ephemeral token
        const response = await bspApi.initializeBSP(agreeability, productContext)
        const ephemeralToken = response.data.client_secret.value

        // Initialize WebRTC connection
        await this.initializeWebRTC(ephemeralToken)

        this.callActive = true
        this.addMessage("system", "Call started. You can begin speaking.")
        this.$emit("call-started")
      } catch (error) {
        console.error("Failed to start call:", error)
        this.$bvToast.toast("Failed to start call. Please try again.", {
          title: "Error",
          variant: "danger",
          solid: true
        })
        this.$emit("call-started")
      }
    },

    async initializeWebRTC (ephemeralToken) {
      try {
        // Create a peer connection
        this.peerConnection = new RTCPeerConnection()

        // Set up to play remote audio from the model
        this.audioElement = document.createElement("audio")
        this.audioElement.autoplay = true
        this.peerConnection.ontrack = e => {
          if (this.audioElement) {
            this.audioElement.srcObject = e.streams[0]
          }
        }

        // Add local audio track for microphone input
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.mediaStream.getTracks().forEach(track => {
          this.peerConnection.addTrack(track, this.mediaStream)
        })

        // Create the data channel
        this.dataChannel = this.peerConnection.createDataChannel("oai-events")
        console.log("Data channel created, state:", this.dataChannel.readyState)

        // Set up ALL data channel event handlers (including 'open')
        this.setupDataChannelHandlers()

        // Start SDP negotiation
        const offer = await this.peerConnection.createOffer()
        await this.peerConnection.setLocalDescription(offer)

        const baseUrl = "https://api.openai.com/v1/realtime"
        const model = "gpt-4o-mini-realtime-preview-2024-12-17"
        console.log(`Sending offer for model: ${model}`)

        const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${ephemeralToken}`,
            "Content-Type": "application/sdp"
          }
        })

        if (!sdpResponse.ok) {
          const errorText = await sdpResponse.text()
          throw new Error(`Failed to get SDP answer: ${sdpResponse.status} ${sdpResponse.statusText} - ${errorText}`)
        }

        const answerSdp = await sdpResponse.text()
        const answer = {
          type: "answer",
          sdp: answerSdp
        }
        console.log("Received SDP answer, setting remote description.")
        await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))
      } catch (error) {
        console.error("Error during WebRTC initialization:", error)
        throw error
      }
    },

    setupDataChannelHandlers () {
      if (!this.dataChannel) {
        console.error("Attempted to set up data channel handlers, but dataChannel is null.")
        return
      }

      // Listener for when the channel opens
      this.dataChannel.addEventListener("open", async () => {
        console.log("Data channel opened! State:", this.dataChannel.readyState)
        // Wait for the session.created event to have been received
        console.log("Waiting for session.created event...")
        await this.sessionSetupPromise
        console.log("Session is ready according to server event.")
        // NOW it's safe to send the initial session setup message
        this.setupSession()
      })

      // Listener for incoming messages
      this.dataChannel.addEventListener("message", (event) => {
        console.log("Data channel message received:", event.data)
        try {
          const serverEvent = JSON.parse(event.data)
          this.handleServerEvent(serverEvent)
        } catch (e) {
          console.error("Failed to parse data channel message:", event.data, e)
        }
      })

      // Optional: Listener for errors on the data channel
      this.dataChannel.addEventListener("error", (error) => {
        console.error("Data Channel Error:", error)
      })

      // Optional: Listener for when the channel closes
      this.dataChannel.addEventListener("close", () => {
        console.log("Data channel closed! State:", this.dataChannel.readyState)
      })
    },

    setupSession () {
      // This function is now called only AFTER the data channel is open and session is created
      if (!this.dataChannel || this.dataChannel.readyState !== "open") {
        console.error("Attempted to call setupSession, but data channel is not open. State:", this.dataChannel ? this.dataChannel.readyState : "null")
        return
      }

      if (!this.sessionReady) {
        console.error("Attempted to call setupSession, but session is not ready.")
        return
      }

      console.log("Setting up session with agreeability:", this.agreeability, "productContext:", this.productContext)

      // --- Send Instructions ---
      const instructionsEvent = {
        type: "session.update",
        session: {
          instructions: `Speak/Reply in the same language as the user. You have must act as the sales prospect with an agreeability level of ${this.agreeability}/100. ${
            this.agreeability < 30
              ? "You are very resistant to sales pitches and will challenge the salesperson."
              : this.agreeability < 70
                ? "You are moderately interested but have concerns and objections."
                : "You are very agreeable and interested in the product."
          } ${
            this.productContext ? `Product context: ${this.productContext}` : ""
          } 
          The user will try to sell you their product. You must act as a sales prospect and respond naturally as a human would in a sales call. Ask relevant questions and provide realistic objections based on your agreeability level.`
        }
      }

      try {
        console.log("Sending session.update instructions event:", JSON.stringify(instructionsEvent))
        this.dataChannel.send(JSON.stringify(instructionsEvent))
      } catch (error) {
        console.error("Error sending session.update instructions event:", error)
        return
      }

      // --- Configure VAD (Disable Auto-Response) ---
      const vadConfigEvent = {
        type: "session.update",
        session: {
          turn_detection: {
            type: "server_vad",
            create_response: false,
            interrupt_response: false
          }
        }
      }

      try {
        console.log("Sending session.update VAD config event:", JSON.stringify(vadConfigEvent))
        this.dataChannel.send(JSON.stringify(vadConfigEvent))
      } catch (error) {
        console.error("Error sending session.update VAD config event:", error)
      }

      // --- Enable Input Audio Transcription ---
      //   const transcriptionConfigEvent = {
      //     type: "session.update",
      //     session: {
      //       input_audio_transcription: {
      //         model: "gpt-4o-mini-transcribe"
      //       }
      //     }
      //   }

      //   try {
      //     console.log("Sending session.update input transcription config event:", JSON.stringify(transcriptionConfigEvent))
      //     this.dataChannel.send(JSON.stringify(transcriptionConfigEvent))
      //   } catch (error) {
      //     console.error("Error sending session.update input transcription config event:", error)
      //   }
    },

    handleServerEvent (event) {
      console.log("Handling server event:", event)

      if (event.type === "session.created") {
        this.addMessage("system", "Session created successfully by server.")
        this.sessionReady = true
        if (this.sessionSetupResolver) {
          this.sessionSetupResolver()
        }
      } else if (event.type === "input_audio_buffer.speech_started") {
        this.isListening = true
        // Optional: You could add a placeholder "You are speaking..." message here
        // this.addMessage("user", "...", false) // Placeholder while speaking
      } else if (event.type === "input_audio_buffer.speech_stopped") {
        this.isListening = false
        console.log("User stopped speaking. Manually triggering response.")
        const createResponseEvent = {
          type: "response.create",
          response: {} // Rely on session instructions
        }
        try {
          if (this.dataChannel && this.dataChannel.readyState === "open") {
            this.dataChannel.send(JSON.stringify(createResponseEvent))
          } else {
            console.warn("Cannot send response.create, data channel not open.")
          }
        } catch (error) {
          console.error("Error sending response.create event:", error)
        }
      } else if (event.type === "input_audio_transcription.delta") {
        if (event.delta) {
          // Add delta transcript for the user
          this.addMessage("user", event.delta, false)
        }
      } else if (event.type === "input_audio_transcription.done") {
        if (event.transcript) {
          console.log("Final user transcript received:", event.transcript)
          // Find the last user message that might be partial and finalize it
          const lastMessageIndex = this.transcript.length - 1
          if (lastMessageIndex >= 0 &&
              this.transcript[lastMessageIndex].role === "user" &&
              !this.transcript[lastMessageIndex].isFinal) {
            // Update the existing partial message with the final content
            // Use Vue.set or splice for reactivity if direct assignment doesn't update view
            this.$set(this.transcript[lastMessageIndex], "content", event.transcript)
            this.$set(this.transcript[lastMessageIndex], "isFinal", true)
          } else {
            // If no partial user message exists, add a new final one.
            this.addMessage("user", event.transcript, true)
          }
        }
      } else if (event.type === "response.text.delta") {
        this.addMessage("assistant", event.delta, false)
      } else if (event.type === "response.done") {
        // Corrected logic for extracting final content
        let finalContent = ""
        // Check if there's output and content
        if (event.response &&
            event.response.output &&
            event.response.output[0] &&
            event.response.output[0].content &&
            event.response.output[0].content[0]) {
          const firstContentPart = event.response.output[0].content[0]
          if (firstContentPart.type === "text") {
            finalContent = firstContentPart.text
          } else if (firstContentPart.type === "audio" && firstContentPart.transcript) { // Check transcript exists
            finalContent = firstContentPart.transcript // Get transcript from audio part
          }
        }

        if (finalContent) {
          console.log("Final response received:", finalContent)
          this.addMessage("assistant", finalContent, true)
        } else {
          console.warn("Received response.done event but couldn't extract final text/transcript:", event)
        }
      } else if (event.type === "error") {
        console.error("Received server error event:", event.error)
        this.addMessage("system", `Error: ${event.error.message} (${event.error.code})`)
      } else {
        console.log("Received unhandled server event type:", event.type, event) // Log the event itself for debugging
      }
    },

    addMessage (role, content, isFinal = true) {
      const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

      if (isFinal) {
        this.transcript.push({ role, content, time })
      } else {
        // For streaming responses, update the last message or add a new one
        if (this.transcript.length > 0 && this.transcript[this.transcript.length - 1].role === role && !this.transcript[this.transcript.length - 1].isFinal) {
          this.transcript[this.transcript.length - 1].content += content
        } else {
          this.transcript.push({ role, content, time, isFinal: false })
        }
      }
    },

    handleEndCall () {
      if (this.peerConnection) {
        this.peerConnection.close()
      }

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop())
      }

      this.addMessage("system", "Call ended.")

      //   // Save transcript to backend
      //   if (this.transcript.length > 0) {
      //     const transcriptText = this.transcript
      //       .filter(msg => msg.role !== "system")
      //       .map(msg => `${msg.role === "user" ? "You" : "AI"}: ${msg.content}`)
      //       .join("\n\n")

      //     bspApi.saveTranscript(transcriptText)
      //       .catch(error => console.error("Failed to save transcript:", error))
      //   }

      this.callActive = false
      this.transcript = []
    }
  },
  beforeDestroy () {
    if (this.callActive) {
      this.handleEndCall()
    }
  }
}
</script>

<style lang="scss" scoped>
.bsp-container {
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
